import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, type ReactElement, type ReactNode } from 'react';

import {
  routinesSpotlightNeedsSidebarOpenAtom,
  routinesSpotlightSidebarAnchorsMountedAtom,
  routinesSpotlightSidebarReadyAtom,
} from '../../../data/workflows/spotlight/routinesSpotlightAtoms';

export interface RoutinesSidebarNavEntryProps {
  isSidebarOpen: boolean;
  onRequestOpenSidebar: () => void;
  /**
   * The onboarding-spotlight coachmark itself reaches a tracked/analytics
   * button (`TrackedButton`), so it is app-only and supplied by the host —
   * this component only tracks the spotlight-tour atoms.
   */
  renderSpotlight: (children: ReactNode) => ReactElement;
  children: ReactNode;
}

/**
 * Chat-sidebar Routines row chrome for the onboarding tour. Tracks the
 * spotlight-tour atoms (anchors mounted, sidebar ready, needs-open) and
 * hands rendering of the coachmark wrapper to the host via `renderSpotlight`.
 */
export function RoutinesSidebarNavEntry({
  isSidebarOpen,
  onRequestOpenSidebar,
  renderSpotlight,
  children,
}: RoutinesSidebarNavEntryProps): ReactElement {
  const setAnchorsMounted = useSetAtom(routinesSpotlightSidebarAnchorsMountedAtom);
  const setSidebarReady = useSetAtom(routinesSpotlightSidebarReadyAtom);
  const needsOpen = useAtomValue(routinesSpotlightNeedsSidebarOpenAtom);

  useEffect(() => {
    setAnchorsMounted(true);
    return () => setAnchorsMounted(false);
  }, [setAnchorsMounted]);

  useEffect(() => {
    setSidebarReady(isSidebarOpen);
    return () => setSidebarReady(false);
  }, [isSidebarOpen, setSidebarReady]);

  useEffect(() => {
    if (needsOpen && !isSidebarOpen) {
      onRequestOpenSidebar();
    }
  }, [needsOpen, isSidebarOpen, onRequestOpenSidebar]);

  return renderSpotlight(children);
}
